/**
 * Feb 2026 · Leave Ledger Report
 * Per-employee, per-leave-type balance/consumed/credit/debit statement
 * for a calendar year. HR shares or exports as CSV.
 *
 * Feb 2026 update · Every balance cell is clickable — opens a drilldown
 * dialog listing every leave request + manual adjustment that produced
 * that balance so HR has full audit traceability.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, RefreshCw, Search, X } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const fmt = (n) => (n === undefined || n === null || n === 0) ? "—" : Number(n).toFixed(2);
const fmtSigned = (n) => {
  const v = Number(n) || 0;
  if (!v) return "0.00";
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}`;
};

// Feb 2026 · Client-side CSV builder for the drilldown dialog.
// Concatenates a labelled totals header, followed by three tabular
// sections (Carry-forward, Manual adjustments, Leave requests) in one
// file so HR can share the full audit trail as a single attachment.
function _csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function _csvRow(cells) { return cells.map(_csvEscape).join(","); }
function _drilldownToCsv(d) {
  const lines = [];
  lines.push(_csvRow(["LEAVE LEDGER DRILLDOWN"]));
  lines.push(_csvRow(["Employee", d.user?.name || "", d.user?.email || ""]));
  lines.push(_csvRow(["Leave type", `${d.leave_type?.code || ""} · ${d.leave_type?.name || ""}`]));
  lines.push(_csvRow(["Year", d.year]));
  lines.push("");
  lines.push(_csvRow(["Totals"]));
  const t = d.totals || {};
  lines.push(_csvRow(["Allocation", t.allocation ?? 0]));
  lines.push(_csvRow(["Carry-forward", t.opening_carry ?? 0]));
  lines.push(_csvRow(["Credits", t.credits ?? 0]));
  lines.push(_csvRow(["Debits", t.debits ?? 0]));
  lines.push(_csvRow(["Consumed", t.consumed ?? 0]));
  lines.push(_csvRow(["Pending", t.pending ?? 0]));
  lines.push(_csvRow(["Balance", t.balance ?? 0]));
  lines.push("");
  const opening = d.opening_adjustments || [];
  if (opening.length) {
    lines.push(_csvRow([`Carry-forward adjustments (before ${d.year})`]));
    lines.push(_csvRow(["Effective date", "Delta (days)", "Reason", "Recorded by"]));
    opening.forEach((a) => lines.push(_csvRow([a.effective_date, a.delta_days, a.reason || a.note || "", a.created_by_name || a.created_by || ""])));
    lines.push("");
  }
  const adjs = d.adjustments || [];
  lines.push(_csvRow([`Manual adjustments (${d.year})`]));
  lines.push(_csvRow(["Effective date", "Delta (days)", "Reason", "Recorded by"]));
  adjs.forEach((a) => lines.push(_csvRow([a.effective_date, a.delta_days, a.reason || a.note || "", a.created_by_name || a.created_by || ""])));
  lines.push("");
  const leaves = d.leaves || [];
  lines.push(_csvRow(["Leave requests"]));
  lines.push(_csvRow(["From", "To", "Days", "Status", "Reason", "Approver"]));
  leaves.forEach((l) => lines.push(_csvRow([l.from_date, l.to_date, l.days, l.status, l.reason || "", l.approved_by_name || ""])));
  return lines.join("\r\n");
}

export default function LeaveLedgerTab({ wsId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ year, types: [], rows: [] });
  // Feb 2026 · Drilldown state — `null` = closed. Otherwise
  // `{user, type}` triggers the LedgerDrilldownDialog to fetch and show
  // the exact records that fed the balance cell.
  const [drilldown, setDrilldown] = useState(null);

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/hr-admin/leave-ledger`, { params: { year } });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load ledger")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, year]);

  // Pivot rows into { user_id, name, email, byType: { type_id: row } } for wide layout.
  const byUser = useMemo(() => {
    const m = new Map();
    const term = q.trim().toLowerCase();
    for (const r of data.rows) {
      if (term && !(r.user_name || "").toLowerCase().includes(term) && !(r.user_email || "").toLowerCase().includes(term)) continue;
      if (!m.has(r.user_id)) m.set(r.user_id, { user_id: r.user_id, name: r.user_name, email: r.user_email, byType: {} });
      m.get(r.user_id).byType[r.leave_type_id] = r;
    }
    return [...m.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data, q]);

  const exportCsv = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/v1/workspaces/${wsId}/hr-admin/leave-ledger/export?year=${year}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("co_token")}` },
    })
      .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = `leave-ledger-${year}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success("Leave ledger CSV downloaded");
      })
      .catch((e) => toast.error(apiErrorMessage(e, "Export failed")));
  };

  // Feb 2026 · Balance cells → drilldown. `focus` tells the dialog which
  // bucket to auto-scroll to (e.g. clicking "Used" focuses the leaves list).
  const openDrill = (user, type, focus = "all") => {
    if (!user || !type) return;
    setDrilldown({ user, type, focus });
  };

  const cellCls = "text-right px-2 py-2 font-mono border-r border-border cursor-pointer hover:bg-primary/10 transition-colors";

  return (
    <div className="space-y-4" data-testid="leave-ledger-tab">
      <div className="border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
        <strong>LEDGER</strong> Per-employee balance = allocation + carry-forward + credits + debits − approved leaves. <b>Click any cell</b> to see the exact requests and adjustments that produced it. Credit/debit adjustments come from HR Admin → Leaves → Adjust balance.
      </div>
      <div className="flex items-center gap-2 flex-wrap border border-border px-3 py-2 bg-muted/20">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-8 w-28 text-xs rounded-none" data-testid="ledger-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[year - 2, year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee…" className="h-8 pl-7 text-xs rounded-none w-56" data-testid="ledger-search" />
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={load} disabled={busy} data-testid="ledger-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={exportCsv} data-testid="ledger-export">
          <Download className="w-3 h-3 mr-1" /> Export CSV
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">{byUser.length} employees · {data.types.length} leave types</span>
      </div>

      <div className="border border-border overflow-auto max-h-[600px]">
        <table className="w-full text-xs border-collapse" data-testid="ledger-table">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th rowSpan={2} className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-r border-border sticky left-0 bg-muted z-20 min-w-[200px]">Employee</th>
              {data.types.map((t) => (
                <th key={t.id} colSpan={5} className="text-center px-3 py-1.5 text-[10px] uppercase tracking-widest text-primary border-r border-b border-border font-bold">{t.code} · {t.name}</th>
              ))}
            </tr>
            <tr>
              {data.types.map((t) => (
                [
                  <th key={t.id + "-alloc"} className="text-right px-2 py-1 text-[9px] uppercase tracking-widest text-muted-foreground border-r border-border">Alloc</th>,
                  <th key={t.id + "-open"} className="text-right px-2 py-1 text-[9px] uppercase tracking-widest text-muted-foreground border-r border-border">Carry</th>,
                  <th key={t.id + "-cred"} className="text-right px-2 py-1 text-[9px] uppercase tracking-widest text-emerald-700 border-r border-border">Cr</th>,
                  <th key={t.id + "-cons"} className="text-right px-2 py-1 text-[9px] uppercase tracking-widest text-red-700 border-r border-border">Used</th>,
                  <th key={t.id + "-bal"} className="text-right px-2 py-1 text-[9px] uppercase tracking-widest text-primary border-r-2 border-primary/50">Bal</th>,
                ]
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {byUser.length === 0 ? (
              <tr><td colSpan={1 + 5 * data.types.length} className="p-8 text-center text-xs italic text-muted-foreground">No leave activity for {year}.</td></tr>
            ) : byUser.map((u) => (
              <tr key={u.user_id} data-testid={`ledger-row-${u.user_id}`} className="hover:bg-muted/30">
                <td className="px-3 py-2 sticky left-0 bg-background z-10 border-r border-border">
                  <div className="font-semibold truncate max-w-[190px]">{u.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>
                </td>
                {data.types.map((t) => {
                  const r = u.byType[t.id];
                  const bal = r?.balance || 0;
                  const user = { id: u.user_id, name: u.name, email: u.email };
                  const type = t;
                  return [
                    <td key={t.id + "-alloc"} className={`${cellCls} text-muted-foreground`}
                        onClick={() => openDrill(user, type, "all")}
                        title="Click for full drilldown"
                        data-testid={`ledger-cell-alloc-${u.user_id}-${t.id}`}>{fmt(r?.allocation)}</td>,
                    <td key={t.id + "-open"}  className={`${cellCls} text-muted-foreground`}
                        onClick={() => openDrill(user, type, "opening")}
                        title="Click to see carry-forward adjustments"
                        data-testid={`ledger-cell-open-${u.user_id}-${t.id}`}>{fmt(r?.opening_carry)}</td>,
                    <td key={t.id + "-cred"}  className={`${cellCls} text-emerald-700`}
                        onClick={() => openDrill(user, type, "credits")}
                        title="Click to see credit adjustments"
                        data-testid={`ledger-cell-cred-${u.user_id}-${t.id}`}>{fmt(r?.credits)}</td>,
                    <td key={t.id + "-cons"}  className={`${cellCls} text-red-700`}
                        onClick={() => openDrill(user, type, "leaves")}
                        title="Click to see the leave requests consumed"
                        data-testid={`ledger-cell-cons-${u.user_id}-${t.id}`}>{fmt(r?.consumed)}</td>,
                    <td key={t.id + "-bal"}   className={`text-right px-2 py-2 font-mono border-r-2 border-primary/50 font-bold cursor-pointer hover:bg-primary/10 transition-colors ${bal < 0 ? "text-red-700" : "text-primary"}`}
                        onClick={() => openDrill(user, type, "all")}
                        title="Click to see full breakdown"
                        data-testid={`ledger-cell-bal-${u.user_id}-${t.id}`}>{r ? Number(bal).toFixed(2) : "—"}</td>,
                  ];
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drilldown && (
        <LedgerDrilldownDialog
          wsId={wsId}
          year={year}
          user={drilldown.user}
          type={drilldown.type}
          focus={drilldown.focus}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}

// ── Drilldown dialog ────────────────────────────────────────────────
// Fetches /leave-ledger/drilldown for one (user, leave_type, year) and
// renders the leaves + adjustments that produced the numbers on the
// Ledger cell that was clicked.
function LedgerDrilldownDialog({ wsId, year, user, type, focus, onClose }) {
  const [busy, setBusy] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    api.get(`/workspaces/${wsId}/hr-admin/leave-ledger/drilldown`, {
      params: { user_id: user.id, leave_type_id: type.id, year },
    })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) toast.error(apiErrorMessage(e, "Failed to load drilldown")); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [wsId, user.id, type.id, year]);

  const totals = data?.totals || {};
  const leaves = data?.leaves || [];
  const adjustments = data?.adjustments || [];
  const opening = data?.opening_adjustments || [];

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-hidden flex flex-col" data-testid="ledger-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="font-mono text-[11px] px-1.5 py-0.5 bg-primary text-primary-foreground uppercase tracking-widest">{type.code}</span>
            <span>{user.name}</span>
            <span className="text-xs text-muted-foreground font-normal">· {year} · {type.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* Totals strip */}
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 text-xs border border-border p-2 bg-muted/30" data-testid="ledger-drilldown-totals">
            <Cell label="Allocation" value={fmt(totals.allocation)} />
            <Cell label="Carry-forward" value={fmtSigned(totals.opening_carry)} />
            <Cell label="Credits" value={fmtSigned(totals.credits)} tone="text-emerald-700" />
            <Cell label="Debits" value={fmtSigned(totals.debits)} tone="text-red-700" />
            <Cell label="Consumed" value={fmt(totals.consumed)} tone="text-red-700" />
            <Cell label="Pending" value={fmt(totals.pending)} tone="text-amber-700" />
            <Cell label="Balance" value={Number(totals.balance || 0).toFixed(2)} tone="text-primary font-bold" />
          </div>

          {busy && <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>}

          {!busy && (
            <>
              {/* Opening / carry-forward */}
              {opening.length > 0 && (
                <Section title={`Carry-forward from before ${year}`} count={opening.length} defaultOpen={focus === "opening"} testid="ledger-drill-opening">
                  <AdjTable rows={opening} />
                </Section>
              )}

              {/* In-year adjustments */}
              <Section title="Manual adjustments (this year)" count={adjustments.length} defaultOpen={focus === "credits" || focus === "debits" || (focus === "all" && !leaves.length)} testid="ledger-drill-adjustments">
                {adjustments.length === 0 ? (
                  <Empty text="No credit/debit adjustments." />
                ) : <AdjTable rows={adjustments} />}
              </Section>

              {/* Leave requests */}
              <Section title="Leave requests" count={leaves.length} defaultOpen={focus === "leaves" || focus === "all"} testid="ledger-drill-leaves">
                {leaves.length === 0 ? (
                  <Empty text="No leave requests in this period." />
                ) : (
                  <table className="w-full text-xs border-collapse" data-testid="ledger-drill-leaves-table">
                    <thead className="bg-muted">
                      <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                        <th className="px-2 py-1">From</th>
                        <th className="px-2 py-1">To</th>
                        <th className="px-2 py-1 text-right">Days</th>
                        <th className="px-2 py-1">Status</th>
                        <th className="px-2 py-1">Reason</th>
                        <th className="px-2 py-1">Approver</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {leaves.map((l) => (
                        <tr key={l.id} className="hover:bg-muted/30" data-testid={`ledger-drill-leave-${l.id}`}>
                          <td className="px-2 py-1 font-mono tabular-nums">{l.from_date}</td>
                          <td className="px-2 py-1 font-mono tabular-nums">{l.to_date}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(l.days || 0).toFixed(2)}</td>
                          <td className="px-2 py-1">
                            <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${l.status === "approved" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : l.status === "rejected" ? "border-red-500 bg-red-50 text-red-800" : l.status === "pending" ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-400 bg-slate-50 text-slate-700"}`}>
                              {l.status}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-muted-foreground truncate max-w-[200px]" title={l.reason || ""}>{l.reason || "—"}</td>
                          <td className="px-2 py-1 text-muted-foreground truncate max-w-[140px]">{l.approved_by_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-none mr-auto"
            disabled={!data || busy}
            onClick={() => {
              if (!data) return;
              const csv = _drilldownToCsv(data);
              const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              const safeName = (user.name || user.email || "employee").replace(/[^A-Za-z0-9_-]+/g, "_");
              a.download = `leave-ledger-${safeName}-${type.code || type.id}-${year}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success("Drilldown CSV downloaded");
            }}
            data-testid="ledger-drilldown-export-csv">
            <Download className="w-3 h-3 mr-1" /> Export CSV
          </Button>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="ledger-drilldown-close">
            <X className="w-3 h-3 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Cell({ label, value, tone }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`font-mono ${tone || ""}`}>{value}</span>
    </div>
  );
}

function Section({ title, count, defaultOpen = true, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  return (
    <div className="border border-border" data-testid={testid}>
      <button type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-muted/40 hover:bg-muted text-[11px] font-semibold uppercase tracking-widest"
        data-testid={`${testid}-toggle`}
      >
        <span>{title} <span className="text-muted-foreground normal-case">· {count}</span></span>
        <span className="text-primary">{open ? "▼" : "▶"}</span>
      </button>
      {open && (
        <div className="overflow-auto">
          {children}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return <div className="p-4 text-center text-xs italic text-muted-foreground">{text}</div>;
}

function AdjTable({ rows }) {
  return (
    <table className="w-full text-xs border-collapse">
      <thead className="bg-muted/40">
        <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
          <th className="px-2 py-1">Effective</th>
          <th className="px-2 py-1 text-right">Delta (days)</th>
          <th className="px-2 py-1">Reason</th>
          <th className="px-2 py-1">Recorded by</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((a) => {
          const d = Number(a.delta_days || 0);
          return (
            <tr key={a.id} className="hover:bg-muted/30" data-testid={`ledger-drill-adj-${a.id}`}>
              <td className="px-2 py-1 font-mono tabular-nums">{a.effective_date || "—"}</td>
              <td className={`px-2 py-1 text-right font-mono ${d > 0 ? "text-emerald-700" : d < 0 ? "text-red-700" : ""}`}>{fmtSigned(d)}</td>
              <td className="px-2 py-1 text-muted-foreground truncate max-w-[280px]" title={a.reason || a.note || ""}>{a.reason || a.note || "—"}</td>
              <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]">{a.created_by_name || a.created_by || "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
